import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { Card, Row, Col, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEye } from "@fortawesome/free-solid-svg-icons";
import AdvanceTable from "components/common/advance-table/advanceTable";
import AdvanceTableProvider from "providers/advanceTableProvider";
import AdvanceTableFooter from "components/common/advance-table/advanceTableFooter";
import AdvanceTableSearchBox from "components/common/advance-table/advanceTableSearchBox";
import useAdvanceTable from "hooks/useAdvanceTable";
import SubtleBadge from "components/common/subtleBadge";
import { getInitialPageSize } from "../../assets/js/common";

const Invoices = ({ customerId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");

  const subscriptionTypeBadgeColors = {
    Trial: "info",
    Standard: "primary",
    Enterprise: "success",
    Basic: "warning"
  };

  const columns = [
    {
      accessorKey: "invoiceNumber",
      header: t("invoiceNumber")
    },
    {
      accessorKey: "invoiceDateText",
      header: t("invoiceDate")
    },
    {
      accessorKey: "billingPeriodText",
      header: t("billingPeriod")
    },
    {
      accessorKey: "subscriptionTypeRaw",
      header: t("plan"),
      cell: ({ row }) => {
        const type = row.original.subscriptionTypeRaw?.trim();

        const normalizedType =
          type?.charAt(0).toUpperCase() + type?.slice(1).toLowerCase();

        return (
          <SubtleBadge
            pill
            bg={subscriptionTypeBadgeColors[normalizedType] || "secondary"}
            className="d-flex flex-center data-font-size"
          >
            <p className="mb-0 data-font-size">
              {t(`subscriptionTypeLable.${normalizedType}`)}
            </p>
          </SubtleBadge>
        );
      }
    },
    {
      accessorKey: "amountText",
      header: t("total")
    },
    {
      accessorKey: "actions",
      header: t("actions"),
      cell: ({ row }) => (
        <div className="d-flex justify-content-center gap-3">
          <FontAwesomeIcon
            title={t("view")}
            icon={faEye}
            className="text-info"
            style={{ cursor: "pointer" }}
            onClick={() => viewInvoice(row.original.fileName)}
          />
          <FontAwesomeIcon
            icon={faDownload}
            className="text-primary"
            title={t("download")}
            style={{ cursor: "pointer" }}
            onClick={() => downloadPdf(row.original.fileName)}
          />
        </div>
      )
    }
  ];

  const table = useAdvanceTable({
    data,
    columns,
    sortable: true,
    pagination: true,
    selection: false,
    perPage: getInitialPageSize('invoiceTable')
  });

  useEffect(() => {
    if (!customerId) return;

    const organization =
      process.env.REACT_APP_ORGANIZATION_NAME?.toLowerCase();

    const paymentServiceUrl =
      process.env.REACT_APP_PAYMENT_SERVICE_URL;

    // Only allow invoice fetch for "getplan"
    if (organization !== "getplan" || !paymentServiceUrl) {
      setData([]);
      return;
    }

    const fetchInvoices = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${paymentServiceUrl}/invoiceRoute/byCustomer/${customerId}`,
          {
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret
            }
          }
        );
        console.log("res",res)

        const sorted = res.data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        const currencySymbols = { INR: '₹', EUR: '€', USD: '$' };

        const transformed = sorted.map(inv => ({
          invoiceNumber: inv.InvoiceNumber,
          fileName: inv.FileName,
          subscriptionTypeRaw: inv.SubscriptionPlan?.trim(),
          billingPeriodText: `${moment(inv.ActivationDate).format("DD-MM-YYYY")} - ${moment(inv.NextBillingDate).format("DD-MM-YYYY")}`,
          invoiceDateText: moment(inv.createdAt).format("DD-MM-YYYY"),
          amountText: `${currencySymbols[inv.Currency] || inv.Currency} ${Number(inv.TotalCost).toFixed(2)}`,
          statusRaw: inv.Status
        }));

        const filteredData = transformed.filter(row => {
          const search = globalFilter.toLowerCase();

          const translatedSubscription =
            t(`subscriptionTypeLable.${row.subscriptionTypeRaw}`)?.toLowerCase() || "";

          const translatedStatus =
            t(row.statusRaw?.toLowerCase())?.toLowerCase() || "";

          return (
            row.invoiceNumber?.toLowerCase().includes(search) ||
            row.invoiceDateText?.toLowerCase().includes(search) ||
            row.billingPeriodText?.toLowerCase().includes(search) ||
            row.amountText?.toLowerCase().includes(search) ||
            row.subscriptionTypeRaw?.toLowerCase().includes(search) ||
            translatedSubscription.includes(search) ||
            row.statusRaw?.toLowerCase().includes(search) ||
            translatedStatus.includes(search)
          );
        });

        setData(filteredData);

      } catch (err) {
        console.warn("Payment service unreachable.");
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();

  }, [customerId, globalFilter, t]);

  const downloadPdf = async (fileName) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_PAYMENT_SERVICE_URL}/invoiceRoute/download/${customerId}/${fileName}?type=download`,
        {
          responseType: "blob",
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();

    } catch (err) {
      console.error("Download failed", err);
    }
  };

  const viewInvoice = async (fileName) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_PAYMENT_SERVICE_URL}/invoiceRoute/download/${customerId}/${fileName}?type=view`,
        {
          responseType: "blob",
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      window.open(url);

    } catch (err) {
      console.error("View failed", err);
    }
  };

  return (
    <AdvanceTableProvider {...table}>
      <Card>
        <Card.Header>
          <Row>
            <Col xs={12} sm="auto" className="ms-auto text-end">
              <AdvanceTableSearchBox setGlobalFilter={setGlobalFilter} />
            </Col>
          </Row>
        </Card.Header>

        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <AdvanceTable
                headerClassName="bg-200 text-nowrap align-middle text-center"
                rowClassName="align-middle white-space-nowrap"
                tableProps={{
                  bordered: true,
                  striped: true,
                  size: "sm",
                  className: "mb-0 overflow-hidden table-hover"
                }}
              />
              {data.length === 0 && (
                <div className="text-center py-4 text-muted">
                  {t("noInvoicesAvailable")}
                </div>
              )}
            </>
          )}
        </Card.Body>

        <Card.Footer>
          <AdvanceTableFooter
            rowInfo
            navButtons
            rowsPerPageSelection
            tableKey="invoiceTable"
          />
        </Card.Footer>
      </Card>
    </AdvanceTableProvider>
  );
};

export default Invoices;